/*
Displays the Instagram posts based on marketing cookie consent or provide an alternative view.
It uses React hooks to manage state and sets event listeners for Cookiebot updates. This ensures that user
interactions and cookie settings are respected and handled accordingly.
 */

'use client';

import React, { useEffect, useState } from 'react';
import HIHButton from './HIHButton';
import styles from './InstagramPost.module.scss';

interface Cookiebot {
  renew: () => void;
  consent: {
    marketing: boolean;
  };
}

// Extend the global Window interface to include the Cookiebot object and Instagram embed API
declare global {
  interface Window {
    Cookiebot: Cookiebot;
    instgrm?: {
      Embeds: {
        process: () => void;
      };
    };
  }
}

const INSTAGRAM_EMBED_SCRIPT_SRC = '//www.instagram.com/embed.js';

const InstagramPost = ({
  urlString,
  className,
}: {
  urlString: string;
  className?: string;
}) => {
  const [hasMarketing, setHasMarketing] = useState(false);

  useEffect(() => {
    let waitForCookiebotCount = 0;

    // Function to wait for Cookiebot to be available
    function waitForCookiebot() {
      if (
        typeof window.Cookiebot === 'undefined' ||
        typeof window.Cookiebot.consent === 'undefined' ||
        typeof window.Cookiebot.consent.marketing === 'undefined'
      ) {
        if (waitForCookiebotCount < 40) {
          setTimeout(waitForCookiebot, 250);
          waitForCookiebotCount++;
          return;
        }
      } else {
        if (window.Cookiebot.consent.marketing) {
          setHasMarketing(true);
        }
      }
    }

    waitForCookiebot();

    // Handler for Cookiebot consent change events
    const handleConsentChange = () => {
      if (window.Cookiebot.consent.marketing) {
        setHasMarketing(true);
      } else {
        setHasMarketing(false);
      }
    };

    // Listen for Cookiebot consent update events
    window.addEventListener('CookiebotOnAccept', handleConsentChange);
    window.addEventListener('CookiebotOnDecline', handleConsentChange);

    // Cleanup event listeners on component unmount
    return () => {
      window.removeEventListener('CookiebotOnAccept', handleConsentChange);
      window.removeEventListener('CookiebotOnDecline', handleConsentChange);
    };
  }, []);

  // Load or re-trigger the official Instagram embed script when marketing consent is given
  useEffect(() => {
    if (!hasMarketing) return;

    if (window.instgrm) {
      // Script already loaded – process all unrendered blockquotes
      window.instgrm.Embeds.process();
    } else if (!document.querySelector(`script[src="${INSTAGRAM_EMBED_SCRIPT_SRC}"]`)) {
      // Script not yet in the DOM – add it once; it processes blockquotes automatically on load
      const script = document.createElement('script');
      script.src = INSTAGRAM_EMBED_SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
  }, [hasMarketing]);

  // Return null if no URL is provided
  if (!urlString) {
    return null;
  }

  return (
    <div className={[styles.instagramPost, className].filter(Boolean).join(' ')}>
      {hasMarketing ? (
        // Display Instagram post if marketing consent is given
        <div className={styles.embedWrapper}>
          <blockquote
            className="instagram-media"
            data-instgrm-permalink={urlString}
            data-instgrm-version="14"
            style={{ border: 0, width: '100%', margin: 0 }}
          />
        </div>
      ) : (
        // Display alternative view if marketing consent is not given
        <div className={styles.fallback}>
          <div className={styles.fallbackHeader}>
            <div className={styles.avatar}></div>
            <div className={styles.headerMeta}>
              <div>
                <div className={styles.namePlaceholder}></div>
                <div className={styles.handlePlaceholder}></div>
              </div>
              <div className={styles.followPlaceholder}></div>
            </div>
          </div>
          <div className={styles.fallbackBody}>
            <p className={styles.fallbackTitle}>Instagram Post blockiert</p>
            <p>
              Damit der Instagram Post angezeigt wird, müssen die Marketing-Cookies
              akzeptiert werden.
            </p>
            <div className={styles.fallbackButtonWrapper}>
              <HIHButton
                onClick={() => {
                  window.Cookiebot.renew();
                }}
                title="Cookie-Einstellungen öffnen"
                variant="secondary"
              ></HIHButton>
            </div>
          </div>
          <div className={styles.fallbackLikes}>
            <div className={styles.likesPlaceholder}></div>
          </div>
          <div className={styles.fallbackComments}>
            <div className={styles.commentsPlaceholder}></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstagramPost;
